import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiToken } from '../../auth/AuthProvider';
import { generateReport, downloadReport, ExpenseLineItem, ReceiptMetadata, ReportMetadata } from '../../services/api';

const MILEAGE_RATE = 0.70;

interface Props {
  employeeName: string;
  title: string;
  startDate: string;
  endDate: string;
  expenses: ExpenseLineItem[];
  receipts: ReceiptMetadata[];
  onReset: () => void;
  onBack: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  summary: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 24, marginBottom: 24 },
  row: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 },
  label: { color: '#666' },
  value: { fontWeight: 600 },
  total: { fontSize: 20, fontWeight: 700, color: '#1a365d', borderTop: '2px solid #e2e8f0', paddingTop: 12, marginTop: 12 },
  btn: { background: '#0078d4', color: 'white', border: 'none', padding: '14px 28px', borderRadius: 6, fontSize: 16, cursor: 'pointer', fontWeight: 600 },
  btnDisabled: { background: '#a0aec0', color: 'white', border: 'none', padding: '14px 28px', borderRadius: 6, fontSize: 16, cursor: 'not-allowed', fontWeight: 600 },
  btnOutline: { background: 'white', color: '#333', border: '1px solid #cbd5e0', padding: '14px 28px', borderRadius: 6, fontSize: 16, cursor: 'pointer' },
  btnSecondary: { background: 'white', color: '#0078d4', border: '1px solid #0078d4', padding: '12px 24px', borderRadius: 6, fontSize: 15, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  actions: { display: 'flex', gap: 12 },
  error: { color: '#e53e3e', marginTop: 12, fontSize: 14 },
  // Success styles
  successContainer: { textAlign: 'center' as const, padding: '40px 0' },
  checkmark: { fontSize: 64, color: '#38a169', marginBottom: 16 },
  successTitle: { fontSize: 24, fontWeight: 700, color: '#1a365d', margin: '0 0 8px' },
  successSubtitle: { color: '#666', margin: '0 0 32px', fontSize: 15 },
  downloads: { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 32 },
  links: { display: 'flex', gap: 16, justifyContent: 'center' },
  link: { color: '#0078d4', cursor: 'pointer', fontSize: 14, background: 'none', border: 'none', textDecoration: 'underline' },
};

export default function GenerateStep({ employeeName, title, startDate, endDate, expenses, receipts, onReset, onBack }: Props) {
  const getToken = useApiToken();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<ReportMetadata | null>(null);

  const grandTotal = expenses.reduce((sum, e) => {
    if (e.amount) return sum + e.amount;
    // Fallback for mileage items without pre-calculated amount
    if (e.category === 'Mileage' && e.mileage) {
      const effectiveMileage = e.is_round_trip ? e.mileage * 2 : e.mileage;
      return sum + effectiveMileage * MILEAGE_RATE;
    }
    return sum;
  }, 0);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const token = await getToken();
      const result = await generateReport(token, {
        employee_name: employeeName,
        title,
        date_range_start: startDate,
        date_range_end: endDate,
        expenses,
        receipt_ids: receipts.map((r) => r.receipt_id),
      });
      setReport(result);
    } catch (e: any) {
      setError(e.message || 'Report generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (format: 'pdf' | 'xlsx') => {
    if (!report) return;
    try {
      const token = await getToken();
      await downloadReport(token, report.report_id, format);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Success view
  if (report) {
    return (
      <div style={styles.successContainer}>
        <div style={styles.checkmark}>&#10003;</div>
        <h2 style={styles.successTitle}>Report Generated!</h2>
        {report.title && <p style={{ fontSize: 16, color: '#333', margin: '0 0 8px' }}>{report.title}</p>}
        <p style={styles.successSubtitle}>
          Total: ${report.total_amount.toFixed(2)} &middot; {report.receipt_count} receipt(s) attached
        </p>

        <div style={styles.downloads}>
          <button style={styles.btn} onClick={() => handleDownload('pdf')}>
            Download PDF
          </button>
          <button style={{ ...styles.btnSecondary, cursor: 'pointer' }} onClick={() => handleDownload('xlsx')}>
            Download Excel
          </button>
        </div>

        <div style={styles.links}>
          <button style={styles.link} onClick={() => navigate('/history')}>View History</button>
          <button style={styles.link} onClick={onReset}>Create Another Report</button>
        </div>
      </div>
    );
  }

  // Pre-generation summary view
  return (
    <div>
      <div style={styles.summary}>
        <div style={styles.row}><span style={styles.label}>Report Title</span><span style={styles.value}>{title}</span></div>
        <div style={styles.row}><span style={styles.label}>Employee</span><span style={styles.value}>{employeeName}</span></div>
        <div style={styles.row}><span style={styles.label}>Period</span><span style={styles.value}>{startDate} to {endDate}</span></div>
        <div style={styles.row}><span style={styles.label}>Line Items</span><span style={styles.value}>{expenses.length}</span></div>
        <div style={styles.row}><span style={styles.label}>Receipts Attached</span><span style={styles.value}>{receipts.length}</span></div>
        <div style={{ ...styles.row, ...styles.total }}>
          <span>Grand Total</span><span>${grandTotal.toFixed(2)}</span>
        </div>
      </div>

      <div style={styles.actions}>
        <button style={styles.btnOutline} onClick={onBack} disabled={generating}>Back</button>
        <button style={generating ? styles.btnDisabled : styles.btn} onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating...' : 'Generate Report'}
        </button>
      </div>
      {error && <p style={styles.error}>{error}</p>}
    </div>
  );
}
